import { alphaSort, alphaSorter } from 'vet-bones/utils';

import { ProjectPropertyType } from 'src/constants/project';
import { ModelSnapshot } from 'src/containers/ProjectDiagram/types/model';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { getInheritedRelationshipsByClass } from 'src/containers/ProjectDiagram/utils/getInheritedRelationshipsByClass';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import {
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_COMMENT_IRI,
  RDFS_LABEL_IRI,
} from 'src/templates/queries/iris';
import { escapeDoubleQuotes } from 'src/utils/escapeDoubleQuotes';
import { isIriInProjectModels } from 'src/utils/isIriInProjectModels';
import { getPropertyIrisForModelConcept } from 'src/utils/stringify/getPropertyIrisForModelConcept';

const modelConceptRelationshipsSort = alphaSort(
  (item: [string, any]) => item[0]
);

export const stringifyModelConceptRelationships = ({
  classIris,
  filter,
  model,
  projectModelIds,
  projectPropertyType,
  targetModelId,
}: {
  classIris?: string[];
  filter?: (
    relationship: [
      string,
      {
        domains: Set<string>;
        ranges: Set<string>;
      }
    ]
  ) => boolean;
  model: ModelSnapshot;
  projectModelIds?: Set<string>;
  projectPropertyType: ProjectPropertyType;
  targetModelId: string;
}): string => {
  const relationshipsByIri = model.linkDataArray
    .filter(
      (linkData) =>
        isModelConceptRelationship(linkData) &&
        (!projectModelIds ||
          isIriInProjectModels(
            linkData.value,
            projectModelIds,
            model.modelData.modelsByIri,
            targetModelId
          ))
    )
    .reduce<
      Record<
        string,
        {
          domains: Set<string>;
          ranges: Set<string>;
        }
      >
    >((acc, data) => {
      if (!acc[data.value]) {
        acc[data.value] = {
          domains: new Set(),
          ranges: new Set(),
        };
      }

      if (data.from) {
        const from = getModelConceptClassValue(data.from);
        acc[data.value].domains.add(from);
      }
      if (data.to) {
        const to = getModelConceptClassValue(data.to);
        acc[data.value].ranges.add(to);
      }

      return acc;
    }, {});

  if (classIris) {
    const inheritedRelationshipsByClass = getInheritedRelationshipsByClass(
      model,
      classIris
    );

    Object.keys(inheritedRelationshipsByClass).forEach((classIri) => {
      Object.keys(inheritedRelationshipsByClass[classIri]).forEach(
        (relationshipIri) => {
          const obj =
            inheritedRelationshipsByClass[classIri]?.[relationshipIri];
          relationshipsByIri[relationshipIri] = {
            ...relationshipsByIri[relationshipIri],
            domains: new Set([
              ...relationshipsByIri[relationshipIri].domains,
              ...obj.domains,
            ]),
            ranges: new Set([
              ...relationshipsByIri[relationshipIri].ranges,
              ...obj.ranges,
            ]),
          };
        }
      );
    });
  }

  let relationships = Object.entries(relationshipsByIri);
  if (filter) {
    relationships = relationships.filter(filter);
  }

  return relationships
    .sort(modelConceptRelationshipsSort)
    .map(([value, { domains, ranges }]) => {
      const lines = [`<${value}> a <${OWL_OBJECT_PROPERTY_IRI}>`];

      const label = model.modelData.labelsByIri[value];
      if (label) {
        lines.push(`<${RDFS_LABEL_IRI}> "${escapeDoubleQuotes(label)}"`);
      }

      const description = model.modelData.descriptionsByIri[value];
      if (description) {
        lines.push(
          `<${RDFS_COMMENT_IRI}> "${escapeDoubleQuotes(description)}"`
        );
      }

      const { domainIri, rangeIri } = getPropertyIrisForModelConcept(
        projectPropertyType,
        model,
        value
      );

      if (domains.size) {
        const objects = Array.from(domains)
          .map((object) => `<${object}>`)
          .sort(alphaSorter)
          .join(', ');
        lines.push(`<${domainIri}> ${objects}`);
      }

      if (ranges.size) {
        const objects = Array.from(ranges)
          .map((object) => `<${object}>`)
          .sort(alphaSorter)
          .join(', ');
        lines.push(`<${rangeIri}> ${objects}`);
      }

      return `${lines.join(' ;\n  ')} .`;
    })
    .join('\n');
};
